import React from "react";
import {
  IoIosCheckmark,
  IoIosClose,
  IoIosArrowRoundForward,
} from "react-icons/io";
import BulbMark from "../../component/BulbMark";
import { FormatNumber, FormatDate } from "../../helpers";
import {
  publicConfirmationGet,
  publicConfirmationAct,
  KIND_UTANG,
} from "../../api";
import "./debt.css";
import "./confirmation.css";

const Confirmation = (props) => {
  const token = props.match.params.token;

  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [view, setView] = React.useState("detail"); // detail|dispute|thanks|gone
  const [reason, setReason] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    publicConfirmationGet(token)
      .then((r) => {
        if (cancelled) return;
        setData(r.data);
        if (
          r.data.state === "confirmed" ||
          r.data.state === "disputed" ||
          r.data.state === "acknowledged" ||
          r.data.state === "expired"
        ) {
          setView("gone");
        }
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err.response?.data?.error?.message ||
            "Link tidak valid atau sudah kedaluwarsa"
        );
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const submit = async (action, payload = {}) => {
    setSubmitting(true);
    try {
      const r = await publicConfirmationAct(token, { action, ...payload });
      setData(r.data);
      setView("thanks");
    } catch (err) {
      setError(
        err.response?.data?.error?.message || "Gagal mengirim respon"
      );
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="cf-page">
        <div className="cf-card cf-card--center">
          <BulbMark size={48} />
          <p style={{ marginTop: "1rem", color: "var(--ink-muted)" }}>
            Memuat…
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cf-page">
        <div className="cf-card cf-card--center">
          <BulbMark size={48} />
          <h2 className="cf-headline">Link tidak valid</h2>
          <p className="cf-sub">{error}</p>
        </div>
      </div>
    );
  }

  const isUtang = data.kind === KIND_UTANG;
  // Note: from PARTY'S perspective the labeling flips:
  //  - kind=utang in user's view → "Anda yang dipinjami / saya pinjam dari Anda"
  //  - kind=piutang in user's view → "Anda yang berutang ke saya / saya yang meminjamkan"
  const youSentence = isUtang
    ? `${data.userDisplayName} mengaku berutang Rp ${FormatNumber(
        Math.round(data.originalAmount)
      )} kepada Anda`
    : `${data.userDisplayName} mencatat bahwa Anda berutang Rp ${FormatNumber(
        Math.round(data.originalAmount)
      )}`;

  return (
    <div className="cf-page">
      <div className="cf-container">
        <header className="cf-brand">
          <BulbMark size={32} />
          <span className="cf-brand-name">cashflow</span>
        </header>

        {view === "detail" && (
          <div className="cf-card">
            <h1 className="cf-headline">
              Konfirmasi catatan{" "}
              {isUtang ? "utang" : "piutang"}
            </h1>
            <p className="cf-sub">{youSentence}</p>

            <div className="cf-fact-list">
              <div className="cf-fact">
                <span className="cf-fact-icon">💰</span>
                <div>
                  <div className="cf-fact-label">Jumlah</div>
                  <div className="cf-fact-value">
                    Rp {FormatNumber(Math.round(data.originalAmount))}
                  </div>
                </div>
              </div>
              {data.dueDate && (
                <div className="cf-fact">
                  <span className="cf-fact-icon">📅</span>
                  <div>
                    <div className="cf-fact-label">Jatuh tempo</div>
                    <div className="cf-fact-value">
                      {FormatDate(data.dueDate)}
                    </div>
                  </div>
                </div>
              )}
              {data.description && (
                <div className="cf-fact">
                  <span className="cf-fact-icon">📝</span>
                  <div>
                    <div className="cf-fact-label">Keterangan</div>
                    <div className="cf-fact-value">{data.description}</div>
                  </div>
                </div>
              )}
            </div>

            <p className="cf-question">Apakah ini benar?</p>

            <div className="cf-actions">
              <button
                className="cf-btn cf-btn--primary"
                onClick={() => submit("confirm")}
                disabled={submitting}
              >
                <IoIosCheckmark size="1.5em" /> Benar, ini saya
              </button>
              <button
                className="cf-btn cf-btn--ghost"
                onClick={() => setView("dispute")}
                disabled={submitting}
              >
                <IoIosClose size="1.5em" /> Bantah jumlah/info
              </button>
            </div>

            <small className="cf-meta">
              Link berlaku sampai {FormatDate(data.expiresAt)} • via Cashflow
            </small>
          </div>
        )}

        {view === "dispute" && (
          <div className="cf-card">
            <h1 className="cf-headline">Bantah</h1>
            <p className="cf-sub">
              Apa yang salah? Jelaskan singkat, {data.userDisplayName} akan
              melihat alasan Anda.
            </p>
            <textarea
              className="form-control"
              rows="4"
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, 500))}
              placeholder='contoh: "Jumlahnya Rp 1jt bukan Rp 2jt" atau "Saya belum pernah pinjam"'
              autoFocus
            />
            <small className="cf-meta">
              {reason.length}/500 karakter
            </small>
            <div className="cf-actions cf-actions--row">
              <button
                className="cf-btn cf-btn--ghost"
                onClick={() => setView("detail")}
                disabled={submitting}
              >
                Batal
              </button>
              <button
                className="cf-btn cf-btn--danger"
                onClick={() => submit("dispute", { reason })}
                disabled={submitting || !reason.trim()}
              >
                Kirim Bantahan
              </button>
            </div>
          </div>
        )}

        {view === "thanks" && (
          <div className="cf-card">
            <div className="cf-celebrate">
              <IoIosCheckmark
                size="3em"
                style={{ color: "var(--income)" }}
              />
            </div>
            <h1 className="cf-headline">Terima kasih 🙏</h1>
            <p className="cf-sub">
              {data.state === "confirmed"
                ? `${data.userDisplayName} akan tahu Anda sudah konfirmasi.`
                : `${data.userDisplayName} akan melihat bantahan Anda.`}
            </p>

            <div className="cf-upsell">
              <div className="cf-upsell-title">
                Punya catatan utang lain?
              </div>
              <p className="cf-upsell-body">
                Cashflow gratis untuk catat utang-piutang Anda sendiri. Daftar
                cepat, nama Anda sudah kami catat.
              </p>
              <a
                href="https://cashflow.zcode.id/landing"
                className="cf-btn cf-btn--primary"
                target="_blank"
                rel="noopener noreferrer"
              >
                Daftar Sekarang <IoIosArrowRoundForward size="1.4em" />
              </a>
              <small className="cf-meta cf-meta--center">
                Atau lain kali saja
              </small>
            </div>
          </div>
        )}

        {view === "gone" && data && (
          <div className="cf-card">
            <h1 className="cf-headline">
              {data.state === "expired"
                ? "Link sudah kedaluwarsa"
                : data.state === "confirmed"
                ? "Sudah dikonfirmasi"
                : data.state === "acknowledged"
                ? "Sudah diselesaikan"
                : "Sudah dibantah"}
            </h1>
            <p className="cf-sub">
              {data.state === "expired"
                ? "Minta pengirim mengirim ulang konfirmasi."
                : data.state === "confirmed"
                ? `Anda mengkonfirmasi catatan ini pada ${FormatDate(
                    data.confirmedAt
                  )}.`
                : data.state === "acknowledged"
                ? `${data.userDisplayName} menerima keberatan Anda pada ${FormatDate(
                    data.resolvedAt
                  )}. Catatan tetap dipertahankan di sisi mereka.`
                : `Anda membantah catatan ini pada ${FormatDate(
                    data.disputedAt
                  )}.`}
            </p>
            {data.state === "disputed" && data.disputeReason && (
              <div className="debt-confirm__dispute" style={{ marginTop: "0.5rem" }}>
                "{data.disputeReason}"
              </div>
            )}
            {data.state === "acknowledged" && data.resolutionNote && (
              <div className="debt-confirm__dispute" style={{
                marginTop: "0.5rem",
                background: "var(--primary-soft)",
                color: "var(--primary-ink)",
              }}>
                Catatan dari {data.userDisplayName}: "{data.resolutionNote}"
              </div>
            )}
          </div>
        )}

        <footer className="cf-footer">
          via <strong>Cashflow</strong> · zcode.id
        </footer>
      </div>
    </div>
  );
};

export default Confirmation;
